import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import {
  CustomTable,
  TD,
  PaginationTR,
  ThLabel,
  Th,
  ExtraActionsHeader,
  ExtraActionsTableData,
  TdLabel,
  ExtraActionsHeaderInvisibleContentWrapper,
  TableResponsive,
  ThWrapper,
  ThIcon,
  TableRow,
  CardList,
  CardHeader,
  CardHeaderLabelWrapper,
  CardHeaderLabel,
  Card,
  CardValueWrapper,
  CardValue,
  CardLeftSide,
  CardRightSide,
  ExtraActionsCard,
  CardWrapper,
  PaginationWrapper,
  CardValueHolder,
  ExtraActionsWrapper,
  LoadMoreWrapper,
  StickyTableWrapper,
  StickyHeader,
  StickyHeaderScroller,
} from './Table.styled';
import Button from 'components/Button/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  excludedTableDataKeys,
  LOAD_MORE_ROOT_MARGIN,
  MAX_TD_LABEL_VALUE_LENGTH_TABLET,
  MAX_TD_LABEL_VALUE_LENGTH,
} from './constants';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import Tooltip from 'components/Tooltip/Tooltip';
import {
  Archive,
  BookOpenText,
  PencilSimple,
  PencilSimpleLine,
  Trash,
} from '@phosphor-icons/react';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import {
  CellType,
  ITableConfig,
  ITableRowConfig,
  SortDirection,
} from './types';
import CardSkeleton from './CardSkeleton/CardSkeleton';
import TableSkeleton from './TableSkeleton/TableSkeleton';
import { getSortIcon, sortableFieldsMapper } from './helpers';
import Pagination from 'components/Pagination/Pagination';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import { useLoadNextPage } from 'utils/hooks/useLoadNextPage';
import { useSyncStickyHeaderWidth } from './hooks';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query';

interface ISortTableParams {
  sortBy?: string;
  setSortBy?: (sortBy: string) => void;
  sortDirection?: SortDirection;
  setSortDirection?: Dispatch<SetStateAction<SortDirection | undefined>>;
}

interface ITableProps {
  page?: number;
  perPage: number;
  total?: number;
  tableData: any[];
  isLoading?: boolean;
  tableConfig: ITableConfig;
  labels?: any[];
  hasExtraAction: boolean;
  extraActionLabel?: string;
  onDelete?: (item: any) => void;
  onEdit?: (item: any) => void;
  onArchive?: (item: any) => void;
  onRestore?: (item: any) => void;
  handlePermissionChange?: any;
  withoutPagination?: boolean;
  withoutMarginTop?: boolean;
  loadPage?: (page: number) => void;
  onViewButtonClick?: (params: any) => void;
  // Infinite scroll replaces the pagination footer with a sentinel row that
  // pulls the next page in as it comes into view.
  isInfiniteScroll?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<any, unknown>>;
  isFetchingNextPage?: boolean;
  // Row the user opened before coming back to this list. Wears the hover
  // styling until they start pointing at rows themselves.
  highlightedRowId?: string;
  // Keeps the header visible while the page scrolls, down to the card layout.
  // The header is then rendered as a separate table - see StickyHeader.
  isStickyHeader?: boolean;
  // Height (px) of page content stuck above the table, which the sticky
  // header has to stack below instead of sliding under.
  stickyHeaderOffset?: number;
  pwId?: string;
  sortParams?: ISortTableParams;
  tableFooter?: ({ tableData }: any) => JSX.Element;
  allowTextSelection?: boolean;
}

export const Table = ({
  page = 1,
  perPage,
  total = 0,
  tableData,
  tableConfig,
  labels,
  hasExtraAction,
  extraActionLabel,
  isLoading,
  onDelete,
  loadPage,
  onViewButtonClick,
  onEdit,
  onArchive,
  onRestore,
  handlePermissionChange,
  withoutPagination = false,
  withoutMarginTop = false,
  pwId,
  sortParams,
  tableFooter,
  allowTextSelection,
  isInfiniteScroll = false,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage = false,
  highlightedRowId,
  isStickyHeader = false,
  stickyHeaderOffset = 0,
}: ITableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translatableColumnIndexes: number[] = [];
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
  const [isHighlightActive, setIsHighlightActive] = useState<boolean>(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const stickyHeaderScrollerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderTableRef = useRef<HTMLTableElement>(null);
  const bodyScrollerRef = useRef<HTMLDivElement>(null);
  const bodyTableRef = useRef<HTMLTableElement>(null);

  // The sticky header sits outside the horizontally scrolling body, so it has
  // to be moved along with it. Set directly rather than through state to keep
  // it in step with the scroll.
  const handleBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (stickyHeaderScrollerRef.current) {
      stickyHeaderScrollerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleMouseEnter = (index: number) => {
    setHoveredGroup(index);
    // The highlight is only a breadcrumb back to where the user was, so once
    // they start hovering rows themselves it has done its job.
    setIsHighlightActive(false);
  };

  const handleMouseLeave = () => {
    setHoveredGroup(null);
  };
  // let customerColumnIndex: number;  // use it for connected quotation
  const { isSmallTablet, isMidTablet } = useBreakpointFlag();

  useSyncStickyHeaderWidth(
    isStickyHeader && !isSmallTablet,
    stickyHeaderScrollerRef,
    stickyHeaderTableRef,
    bodyScrollerRef,
    bodyTableRef
  );

  useLoadNextPage(
    loadMoreRef,
    isInfiniteScroll && hasNextPage,
    fetchNextPage,
    isLoading || isFetchingNextPage,
    LOAD_MORE_ROOT_MARGIN
  );

  const isPaginationPresent = total > perPage;
  const showPagination =
    isPaginationPresent &&
    !withoutPagination &&
    !isInfiniteScroll &&
    !!loadPage;

  // The rows are keyed off the data rather than the config, so the colSpan of
  // the footer rows has to be counted the same way.
  const getColumnCount = () =>
    Object.keys(tableData[0]).filter(
      (key) => !excludedTableDataKeys.includes(key)
    ).length + 2;

  let sortBy: string,
    setSortBy: ((sortBy: string) => void) | undefined,
    sortDirection: SortDirection,
    setSortDirection:
      | Dispatch<SetStateAction<SortDirection | undefined>>
      | undefined;

  if (sortParams) {
    sortBy = sortParams.sortBy ?? '';
    setSortBy = sortParams.setSortBy;
    sortDirection = sortParams.sortDirection ?? SortDirection.NONE;
    setSortDirection = sortParams.setSortDirection;
  }

  const handleSortClick = (rowConfig: ITableRowConfig) => {
    switch (sortDirection) {
      case SortDirection.NONE: {
        setSortBy?.(sortableFieldsMapper(rowConfig));
        setSortDirection?.(SortDirection.ASC);
        break;
      }
      case SortDirection.ASC: {
        if (sortBy !== sortableFieldsMapper(rowConfig)) {
          setSortDirection?.(SortDirection.ASC);
        } else {
          setSortDirection?.(SortDirection.DESC);
        }
        setSortBy?.(sortableFieldsMapper(rowConfig));

        break;
      }
      case SortDirection.DESC: {
        if (sortBy !== sortableFieldsMapper(rowConfig)) {
          setSortDirection?.(SortDirection.ASC);
        } else {
          setSortDirection?.(SortDirection.NONE);
        }
        setSortBy?.(sortableFieldsMapper(rowConfig));
        break;
      }
      default:
        break;
    }
  };

  const renderTableHeaderData = () => {
    return (
      <tr
        style={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          width: '100%',
        }}
      >
        <th></th>
        {tableConfig?.rowConfigs.map((rowConfig: ITableRowConfig, index) => {
          // if (rowConfig.header === 'Customer') customerColumnIndex = index; // use it for connected quotation
          if (
            rowConfig.header === 'Payment' ||
            rowConfig.header === 'Status' ||
            rowConfig.header === 'Sub Status' ||
            rowConfig.header === 'Type' ||
            rowConfig.header === 'License'
          ) {
            translatableColumnIndexes.push(index);
          }
          const isSortable =
            setSortBy && setSortDirection && rowConfig.isSortable !== false;
          return (
            <Th
              key={index}
              style={{ position: 'relative' }}
              onClick={
                isSortable ? () => handleSortClick(rowConfig) : undefined
              }
              flexValue={rowConfig.flexValue}
              $bigCell={rowConfig?.bigCell}
            >
              <ThWrapper>
                <ThLabel
                  data-testid={`${pwId}-${rowConfig.header}`}
                  isClickable={isSortable}
                >
                  {t(rowConfig.header)}
                </ThLabel>
                {isSortable && (
                  <Icon
                    svg={getSortIcon(sortDirection, sortBy, rowConfig)}
                    marginLeft="5rem"
                    color={COLORS.WHITE}
                    size={17}
                  />
                )}
              </ThWrapper>
              {rowConfig?.cellType === CellType.PERMISSIONS && rowConfig.icon && (
                <ThIcon>
                  <Tooltip content={t('Read permissions')}>
                    <Icon svg={BookOpenText} size={20} color={COLORS.WHITE} />
                  </Tooltip>
                  <Tooltip content={t('Write permissions')}>
                    <Icon
                      svg={PencilSimpleLine}
                      size={20}
                      color={COLORS.WHITE}
                    />
                  </Tooltip>
                </ThIcon>
              )}
            </Th>
          );
        })}
        <ExtraActionsHeader
          extraActionsFlexValue={tableConfig.extraActionsFlexValue}
        >
          <ExtraActionsHeaderInvisibleContentWrapper>
            {onDelete && (
              <Icon
                svg={Trash}
                color={COLORS.RED_100}
                size={20}
                weight="regular"
              />
            )}
            {onEdit && (
              <Icon
                svg={PencilSimple}
                color={COLORS.GREEN}
                size={20}
                weight="regular"
              />
            )}
            {onArchive && !onRestore && (
              <Icon
                svg={Archive}
                color={COLORS.RED_100}
                size={20}
                weight="regular"
              />
            )}

            {(onRestore || hasExtraAction) && (
              <Button table label={extraActionLabel || t('View')} />
            )}
          </ExtraActionsHeaderInvisibleContentWrapper>
        </ExtraActionsHeader>
        {tableConfig?.extraActionsFlexValue &&
          tableConfig.extraActionsFlexValue > 0 && <th></th>}
      </tr>
    );
  };

  const getTdLabelValue = (tdIndex: number, rowData: any, rowDataKey: any) => {
    const maxLength = isMidTablet
      ? MAX_TD_LABEL_VALUE_LENGTH_TABLET
      : MAX_TD_LABEL_VALUE_LENGTH;

    const tdLabelValue = translatableColumnIndexes.includes(tdIndex)
      ? t(rowData[rowDataKey])
      : rowData[rowDataKey];
    return (rowDataKey === 'externalId' ||
      rowDataKey === 'reference' ||
      rowDataKey === 'city') &&
      tdLabelValue?.length > maxLength ? (
      <Tooltip content={tdLabelValue}>{`${tdLabelValue?.substring(
        0,
        maxLength
      )}...`}</Tooltip>
    ) : (
      tdLabelValue
    );
  };

  // Always render tableFooter if provided to ensure hooks are called consistently
  // This must be done at the component level, not conditionally inside renderTableBodyData
  const footerElement = tableFooter
    ? tableFooter({ tableData, tableConfig })
    : null;

  const renderTableBodyData = () => {
    return [
      tableData.map((rowData: React.ReactNode[], rowIndex: number) => {
        const isAdminChecked =
          rowData['adminPermission']?.read || rowData['adminPermission']?.edit;

        return (
          <React.Fragment key={rowIndex}>
            <TableRow
              data-testid={`${pwId}-row-${rowIndex}`}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flex: 1,
                width: '100%',
              }}
              isHovered={
                hoveredGroup === rowIndex ||
                (isHighlightActive &&
                  !!highlightedRowId &&
                  rowData['navigationID'] === highlightedRowId)
              }
              onMouseEnter={() => handleMouseEnter(rowIndex)}
              onMouseLeave={handleMouseLeave}
            >
              <td></td>
              {Object.keys(rowData)
                .filter((key) => !excludedTableDataKeys.includes(key))
                .map((rowDataKey: any, tdIndex: number) => {
                  const rowConfig: ITableRowConfig =
                    tableConfig.rowConfigs?.[tdIndex];

                  return (
                    <TD
                      data-testid={`${pwId}-data-cell-${tdIndex}`}
                      flexValue={rowConfig && rowConfig.flexValue}
                      key={`${rowIndex}-${tdIndex}`}
                      onClick={() => {
                        // if (
                        //   rowData['customerID'] &&
                        //   customerColumnIndex === tdIndex
                        // )
                        //   navigate(
                        //     `${SingleCustomerRoutes.SingleCustomerGeneral.fullPath.replace(
                        //       ':id',
                        //       rowData['customerID']
                        //     )}`
                        //   );
                      }}
                    >
                      {/* {rowConfig?.cellType === CellType.PERMISSIONS ? (
                        <PermissionsCell
                          rowDataKey={rowDataKey}
                          user_id={rowData['userId']}
                          permissionDetails={rowData[rowDataKey]}
                          handlePermissionChange={handlePermissionChange}
                          page={page}
                          resetPerms={resetPerms}
                          setResetPerms={setResetPerms}
                          isAdminChecked={isAdminChecked}
                          isFieldLicense={isFieldLicense}
                        />
                      ) : rowConfig?.cellType === CellType.CHECKBOX ? (
                        <CheckboxCell
                          key={`${rowData[rowDataKey]}`}
                          value={rowData[rowDataKey] as boolean}
                          onChange={
                            onCheckboxChange
                              ? () => onCheckboxChange(rowData)
                              : () => null
                          }
                        />
                      ) : (
                        <TdLabel>
                          {getTdLabelValue(tdIndex, rowData, rowDataKey)}
                        </TdLabel>
                      )} */}
                      <TdLabel
                        $allowTextSelection={allowTextSelection}
                        $isUppercase={rowConfig?.isUppercase}
                      >
                        {getTdLabelValue(tdIndex, rowData, rowDataKey)}
                      </TdLabel>
                    </TD>
                  );
                })}
              <ExtraActionsTableData
                data-testId={`${pwId}-data-cell`}
                extraActionsFlexValue={tableConfig.extraActionsFlexValue}
              >
                {onDelete && (
                  <Icon
                    data-testid={`delete-icon-${rowIndex}`}
                    onClick={() => {
                      onDelete(rowData);
                    }}
                    svg={Trash}
                    weight="regular"
                    color={COLORS.RED_100}
                    size={20}
                  />
                )}
                {onEdit && (
                  <Icon
                    data-testid={`edit-icon-${rowIndex}`}
                    onClick={() => {
                      onEdit(rowData);
                    }}
                    svg={PencilSimple}
                    color={COLORS.GREEN}
                    size={20}
                    weight="regular"
                  />
                )}

                {onArchive && !onRestore && (
                  <Icon
                    data-testid={`archive-icon-${rowIndex}`}
                    onClick={() => {
                      onArchive(rowData);
                    }}
                    svg={Archive}
                    color={COLORS.RED_100}
                    size={30}
                    weight="regular"
                  />
                )}

                {hasExtraAction && !onRestore ? (
                  <ExtraActionsWrapper>
                    <Button
                      pwId={`${pwId}-view-button`}
                      table
                      label={extraActionLabel || t('View')}
                      onClick={() =>
                        onViewButtonClick && onViewButtonClick(rowData)
                      }
                    />
                  </ExtraActionsWrapper>
                ) : (
                  onRestore && (
                    <ExtraActionsWrapper>
                      <Button
                        pwId={`${pwId}-view-button`}
                        table
                        label={t('Restore')}
                        onClick={() => onRestore && onRestore(rowData)}
                      />
                    </ExtraActionsWrapper>
                  )
                )}
              </ExtraActionsTableData>
              {tableConfig?.extraActionsFlexValue &&
                tableConfig.extraActionsFlexValue > 0 && <td></td>}
            </TableRow>
          </React.Fragment>
        );
      }),
      footerElement,
      isInfiniteScroll ? renderLoadMoreRow() : renderTablePaginationFooter(),
    ];
  };

  const renderTablePaginationFooter = () => {
    if (!tableData.length) return;
    return (
      <PaginationTR key="0" isPaginationPresent={isPaginationPresent}>
        <td colSpan={getColumnCount()}>
          {showPagination && loadPage && (
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={loadPage}
            />
          )}
        </td>
        {/* {onDelete && <td></td>} */}
      </PaginationTR>
    );
  };

  // Takes the place of the pagination footer: the sentinel sits below the last
  // row, and the loader only shows while a further page is on its way.
  const renderLoadMoreRow = () => {
    if (!tableData.length) return;
    return (
      <PaginationTR key="load-more" isPaginationPresent={false}>
        <td colSpan={getColumnCount()}>
          <LoadMoreWrapper ref={loadMoreRef}>
            {isFetchingNextPage ? <SmallLoader /> : null}
          </LoadMoreWrapper>
        </td>
      </PaginationTR>
    );
  };

  const renderCardHeader = () => {
    return (
      <CardHeader>
        {tableConfig?.rowConfigs.map((rowConfig: ITableRowConfig, index) => {
          // if (rowConfig.header === 'Customer') customerColumnIndex = index; // use it for connected quotation
          if (
            rowConfig.header === 'Payment' ||
            rowConfig.header === 'Status' ||
            rowConfig.header === 'Sub Status' ||
            rowConfig.header === 'Type' ||
            rowConfig.header === 'License'
          ) {
            translatableColumnIndexes.push(index);
          }
          return (
            <CardHeaderLabelWrapper
              key={index}
              selectField={rowConfig.cellType === CellType.SELECT}
            >
              <CardHeaderLabel>{t(rowConfig.header)}:</CardHeaderLabel>
            </CardHeaderLabelWrapper>
          );
        })}
      </CardHeader>
    );
  };

  const renderCard = () => {
    return (
      <CardWrapper>
        {tableData.map((rowData: React.ReactNode[], rowIndex: number) => {
          const isAdminChecked =
            rowData['adminPermission']?.read ||
            rowData['adminPermission']?.edit;

          return (
            <Card data-testid={`row-${rowIndex}`} key={rowIndex}>
              <CardLeftSide>{renderCardHeader()}</CardLeftSide>
              <CardRightSide>
                <CardValueHolder>
                  {Object.keys(rowData)
                    .filter((key) => !excludedTableDataKeys.includes(key))
                    .map((rowDataKey: any, tdIndex: number) => {
                      const rowConfig: ITableRowConfig =
                        tableConfig.rowConfigs?.[tdIndex];

                      return (
                        <CardValueWrapper
                          flexValue={rowConfig && rowConfig.flexValue}
                          key={`${rowIndex}-${tdIndex}`}
                          onClick={() => {
                            // if (
                            //   rowData['customerID'] &&
                            //   customerColumnIndex === tdIndex
                            // )
                            //   navigate(
                            //     `${SingleCustomerRoutes.SingleCustomerGeneral.fullPath.replace(
                            //       ':id',
                            //       rowData['customerID']
                            //     )}`
                            //   );
                          }}
                        >
                          {/* {rowConfig?.cellType === CellType.PERMISSIONS ? (
                            <PermissionsCell
                              rowDataKey={rowDataKey}
                              user_id={rowData['userId']}
                              permissionDetails={rowData[rowDataKey]}
                              handlePermissionChange={handlePermissionChange}
                              page={page}
                              resetPerms={resetPerms}
                              setResetPerms={setResetPerms}
                              isAdminChecked={isAdminChecked}
                              isFieldLicense={isFieldLicense}
                            />
                          ) : rowConfig?.cellType === CellType.CHECKBOX ? (
                            <CheckboxCell
                              key={`${rowData[rowDataKey]}`}
                              value={rowData[rowDataKey] as boolean}
                              onChange={
                                onCheckboxChange
                                  ? () => onCheckboxChange(rowData)
                                  : () => null
                              }
                            />
                          ) : (
                            <CardValue>
                              {translatableColumnIndexes.includes(tdIndex)
                                ? t(rowData[rowDataKey])
                                : rowData[rowDataKey]}
                            </CardValue>
                          )} */}
                          <CardValue $isUppercase={rowConfig?.isUppercase}>
                            {translatableColumnIndexes.includes(tdIndex)
                              ? t(rowData[rowDataKey] as string)
                              : rowData[rowDataKey]}
                          </CardValue>
                        </CardValueWrapper>
                      );
                    })}
                </CardValueHolder>
              </CardRightSide>
              <ExtraActionsCard>
                {onDelete && (
                  <Button
                    redOutline
                    table
                    label={t('Delete')}
                    onClick={() => {
                      onDelete(rowData);
                    }}
                  />
                )}
                {onEdit && (
                  <Button
                    onClick={() => {
                      onEdit(rowData);
                    }}
                    table
                    label={t('Edit')}
                  />
                )}
                {onArchive && !onRestore && (
                  <Button
                    onClick={() => {
                      onArchive(rowData);
                    }}
                    tableRed
                    label={t('Archive')}
                  />
                )}
                {hasExtraAction && !onRestore && (
                  <ExtraActionsWrapper>
                    <Button
                      table
                      label={extraActionLabel || t('View')}
                      onClick={() =>
                        onViewButtonClick && onViewButtonClick(rowData)
                      }
                    />
                  </ExtraActionsWrapper>
                )}
                {onRestore && (
                  <ExtraActionsWrapper>
                    <Button
                      table
                      label={t('Restore')}
                      onClick={() => onRestore && onRestore(rowData)}
                    />
                  </ExtraActionsWrapper>
                )}
              </ExtraActionsCard>
            </Card>
          );
        })}
        {isInfiniteScroll && tableData.length ? (
          <LoadMoreWrapper ref={loadMoreRef}>
            {isFetchingNextPage ? <SmallLoader /> : null}
          </LoadMoreWrapper>
        ) : null}
        {showPagination && loadPage && (
          <PaginationWrapper>
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={loadPage}
            />
          </PaginationWrapper>
        )}
      </CardWrapper>
    );
  };

  return (
    <>
      {isSmallTablet ? (
        <CardList>
          {renderCard()}
          {isLoading && <CardSkeleton numRows={perPage} />}
        </CardList>
      ) : isStickyHeader ? (
        <StickyTableWrapper>
          <StickyHeader
            $withoutPagination={withoutPagination}
            $withoutMarginTop={withoutMarginTop}
            $stickyOffset={stickyHeaderOffset}
          >
            <StickyHeaderScroller ref={stickyHeaderScrollerRef}>
              <CustomTable
                ref={stickyHeaderTableRef}
                $labels={labels}
                $withoutMarginTop
              >
                <thead>{renderTableHeaderData()}</thead>
              </CustomTable>
            </StickyHeaderScroller>
          </StickyHeader>
          <TableResponsive
            ref={bodyScrollerRef}
            className="table-responsive"
            onScroll={handleBodyScroll}
          >
            <CustomTable ref={bodyTableRef} $labels={labels} $withoutMarginTop>
              {isLoading && <TableSkeleton numRows={perPage} numColumns={1} />}
              <tbody>{renderTableBodyData()}</tbody>
            </CustomTable>
          </TableResponsive>
        </StickyTableWrapper>
      ) : (
        <TableResponsive className="table-responsive">
          <CustomTable
            $labels={labels}
            $withoutPagination={withoutPagination}
            $withoutMarginTop={withoutMarginTop}
          >
            <thead>{renderTableHeaderData()}</thead>
            {isLoading && <TableSkeleton numRows={perPage} numColumns={1} />}
            <tbody>{renderTableBodyData()}</tbody>
          </CustomTable>
        </TableResponsive>
      )}
    </>
  );
};

export default Table;
